import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { AppDataService, RiderService, VehicleBrandService } from 'app/services';
import { VehicleBrandModel } from 'app/models/vehicle/vehicle-brand.model';
import { VehicleModel } from 'app/models/vehicle/vehicle-model';
import { UpdateRiderVehicleRequest, EditVehicle } from 'app/models/rider/update-rider-vehicle-request.interface';
import { RiderVehicleType, RiderVehicleTypeEnum } from 'app/models';
import { ComboItemDto } from 'app/merchant/partners-portal/interfaces/combo-item.interface';

@Component({
    selector: 'edit-vehicle-modal',
    templateUrl: './edit-vehicle-modal.component.html',
    styleUrls: ['./edit-vehicle-modal.component.scss']
})
export class EditVehicleModalComponent implements OnInit, OnChanges {
    @Input() visible: boolean = false;
    @Input() selectedVehicle: any;
    /**
     * Scope: active, non-salaried Passenger 4W Ride/TNVS drivers.
     * When true, the Vehicle Type field is editable and limited to Sedan/SUV corrections.
     * When false, the Vehicle Type field is shown as readonly (previous behavior).
     */
    @Input() canEditVehicleType: boolean = false;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() vehicleSaved = new EventEmitter<void>();

    public activeTab: 'details' | 'photos' = 'details';
    public vehicleBrands: VehicleBrandModel[] = [];
    public vehicleModels: VehicleModel[] = [];
    public selectedVehicleBrand: number | null = null;
    public selectedVehicleModel: number | null = null;
    public selectedBrandObj: VehicleBrandModel | null = null;
    public selectedModelObj: VehicleModel | null = null;
    public selectedVehicleTypeObj: any | null = null;
    public editVehicle: EditVehicle = {};
    public brandInputText: string = '';
    public modelInputText: string = '';
    public isVehicleSaving: boolean = false;
    public currentYear: number = new Date().getFullYear();
    public yearOptions: number[] = [];
    public riderVehicleTypeList = this.appData.isDumagueteLocation ? RiderVehicleType.RiderVehicleTypeDumagueteList
        : this.appData.isBoracayLocation ? RiderVehicleType.RiderVehicleTypeBoracayList
            : RiderVehicleType.RiderVehicleTypeXpressList;
    public sedanSuvOptions: Array<{ value: RiderVehicleTypeEnum; name: string }> = [
        { value: RiderVehicleTypeEnum.Sedan, name: 'Sedan' },
        { value: RiderVehicleTypeEnum.SUV, name: 'SUV' }
    ];

    constructor(
        private appData: AppDataService,
        private messageService: MessageService,
        private riderService: RiderService,
        private vehicleBrandService: VehicleBrandService
    ) {}

    ngOnInit(): void {
        const years: number[] = [];
        const maxYear = Math.max(this.currentYear, 2030);
        for (let year = maxYear; year >= 2000; year--) {
            years.push(year);
        }
        this.yearOptions = years;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible && this.selectedVehicle) {
            this.initializeForm();
        }
    }

    private async initializeForm() {
        this.activeTab = 'details';
        this.setEditVehicleFromSelected();
        await this.loadVehicleBrands(this.selectedVehicle?.VehicleTypeId);
    }

    private setEditVehicleFromSelected() {
        if (!this.selectedVehicle) {
            return;
        }
        this.editVehicle = {
            VehicleYear: this.selectedVehicle.VehicleYear,
            VehicleColorText: this.selectedVehicle.VehicleColorText,
            NumberPlate: this.selectedVehicle.NumberPlate,
            RegistrationNumber: this.selectedVehicle.RegistrationNumber,
            PaOrCpcCaseNumber: this.selectedVehicle.PaOrCpcCaseNumber,
            VehicleFrontImageUrl: this.selectedVehicle.VehicleFrontImageUrl,
            VehicleBackImageUrl: this.selectedVehicle.VehicleBackImageUrl,
            VehicleRightSideImageUrl: this.selectedVehicle.VehicleRightSideImageUrl,
            VehicleLeftSideImageUrl: this.selectedVehicle.VehicleLeftSideImageUrl
        };
        this.brandInputText = this.selectedVehicle.VehicleModel;
        this.modelInputText = this.selectedVehicle.VehicleModelSeries;
        const vehicleTypeOptions = this.canEditVehicleType ? this.sedanSuvOptions : this.riderVehicleTypeList;
        this.selectedVehicleTypeObj = vehicleTypeOptions.find(x => x.value === this.selectedVehicle?.VehicleTypeId)
            ?? this.riderVehicleTypeList.find(x => x.value === this.selectedVehicle?.VehicleTypeId);
    }

    private async loadVehicleBrands(vehicleTypeId: number | null | undefined) {
        const dto = {
            VehicleType: vehicleTypeId
        };
        const data = await this.vehicleBrandService.getVehicleBrands(dto, this.messageService);
        if (!data || !data.Items) {
            return;
        }
        this.vehicleBrands = data.Items;
        const brand = this.vehicleBrands.find((b) =>
            b.NameBrand?.toLowerCase() === (this.selectedVehicle?.VehicleModel || '').toLowerCase());
        if (brand) {
            this.selectedVehicleBrand = brand.Id;
            this.selectedBrandObj = brand;
            this.brandInputText = brand.NameBrand;
            await this.loadVehicleModels(brand.Id);
        } else {
            this.selectedVehicleBrand = null;
            this.selectedBrandObj = null;
        }
    }

    private async loadVehicleModels(brandId: number | null | undefined) {
        if (!brandId) {
            this.vehicleModels = [];
            this.selectedVehicleModel = null;
            this.selectedModelObj = null;
            return;
        }
        const dto = {
            VehicleBrandId: brandId,
            VehicleType: this.selectedVehicle?.VehicleTypeId
        };
        const data = await this.vehicleBrandService.getVehicleModels(dto, this.messageService);
        if (!data || !data.Items) {
            this.vehicleModels = [];
            this.selectedVehicleModel = null;
            this.selectedModelObj = null;
            return;
        }
        this.vehicleModels = data.Items;
        const model = this.vehicleModels.find((m) =>
            m.NameModel?.toLowerCase() === (this.selectedVehicle?.VehicleModelSeries || '').toLowerCase());
        if (model) {
            this.selectedVehicleModel = model.Id;
            this.selectedModelObj = model;
            this.modelInputText = model.NameModel;
        } else {
            this.selectedVehicleModel = null;
            this.selectedModelObj = null;
        }
    }

    async onBrandDropdownChange(event: DropdownChangeEvent) {
        if (event.value && typeof event.value === 'object') {
            this.selectedVehicleBrand = event.value.Id;
            this.brandInputText = event.value.NameBrand;
            await this.loadVehicleModels(event.value.Id);
        } else {
            this.selectedVehicleBrand = null;
            this.brandInputText = '';
            this.vehicleModels = [];
            this.selectedModelObj = null;
            this.selectedVehicleModel = null;
            this.modelInputText = '';
        }
    }

    onModelDropdownChange(event: DropdownChangeEvent) {
        if (event.value && typeof event.value === 'object') {
            this.selectedVehicleModel = event.value.Id;
            this.modelInputText = event.value.NameModel;
        } else if (typeof event.value === 'string') {
            this.selectedVehicleModel = null;
            this.modelInputText = event.value;
        }
    }

    switchTab(tab: 'details' | 'photos') {
        this.activeTab = tab;
    }

    closeModal() {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    resetForm() {
        this.setEditVehicleFromSelected();
        this.closeModal();
    }

    async saveVehicle() {
        if (!this.selectedVehicle) {
            return;
        }

        if (!this.selectedVehicleTypeObj) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select a vehicle type from the list' });
            return;
        }

        if (!this.selectedVehicleBrand) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select a vehicle brand from the list' });
            return;
        }

        if (!this.selectedVehicleModel && !this.modelInputText) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Vehicle model is required' });
            return;
        }

        const year = Number(this.editVehicle.VehicleYear);
        if (!year || isNaN(year)) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Vehicle year must be numeric' });
            return;
        }

        if (!this.editVehicle.NumberPlate) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Plate number is required' });
            return;
        }

        const payload: UpdateRiderVehicleRequest = {
            RiderVehicleId: this.selectedVehicle.RiderVehicleId || 0,
            VehicleYear: year,
            NumberPlate: this.editVehicle.NumberPlate || ''
        };

        if (this.editVehicle.VehicleColorText) {
            payload.VehicleColorText = this.editVehicle.VehicleColorText;
        }

        if (this.editVehicle.RegistrationNumber) {
            payload.RegistrationNumber = this.editVehicle.RegistrationNumber;
        }

        payload.VehicleTypeId = this.selectedVehicleTypeObj.value;
        payload.VehicleBrandId = Number(this.selectedVehicleBrand);
        if (this.brandInputText && this.brandInputText.trim()) {
            payload.VehicleBrandName = this.brandInputText.trim();
        }

        const modelId = Number(this.selectedVehicleModel);
        if (!isNaN(modelId) && modelId > 0) {
            payload.VehicleModelId = modelId;
        }

        if (this.modelInputText && this.modelInputText.trim()) {
            payload.VehicleModelName = this.modelInputText.trim();
        }

        if (this.editVehicle.PaOrCpcCaseNumber) {
            payload.PaOrCpcCaseNumber = this.editVehicle.PaOrCpcCaseNumber;
        }

        this.isVehicleSaving = true;
        try {
            const data = await this.riderService.updateRiderVehicle(payload, this.messageService);
            this.isVehicleSaving = false;
            if (!data || !data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage || 'Failed to update vehicle' });
                return;
            }
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Vehicle updated' });
            this.closeModal();
            this.vehicleSaved.emit();
        } catch (err) {
            this.isVehicleSaving = false;
            console.error('Error updating vehicle:', err);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Network error occurred while saving' });
        }
    }
}
